import { useState } from 'react';
import { Loader2, Send, AlertTriangle, CheckCircle2, XCircle, SkipForward } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export function InvoiceGeneratePanel({ entityId }) {
  const [year, setYear] = useState(currentMonth > 1 ? currentYear : currentYear - 1);
  const [month, setMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await invoicingAPI.generate(entityId, year, month);
      setResult(res.data);
      if (res.data.blocked) {
        toast.error(res.data.message);
      } else {
        const parts = [`${res.data.candidates} candidates`];
        if (res.data.created) parts.push(`${res.data.created} created`);
        if (res.data.skipped_already_invoiced) parts.push(`${res.data.skipped_already_invoiced} already invoiced`);
        if (res.data.skipped_no_mapping) parts.push(`${res.data.skipped_no_mapping} no mapping`);
        if (res.data.skipped_cancelled) parts.push(`${res.data.skipped_cancelled} cancelled`);
        if (res.data.failed) parts.push(`${res.data.failed} failed`);
        toast.success(parts.join(', '));
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="h-5 w-5 text-blue-500" />
          Generate Draft Invoices
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Create DRAFT invoices in Pennylane for invoiceable bookings. Preview first, then generate.
          Blocked if VAT rate is missing. Unmapped channels are skipped per-booking.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={generate} disabled={loading} variant="default">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Generate Drafts
          </Button>
        </div>

        {/* Result display */}
        {result && (
          <div className="space-y-3">
            {result.blocked ? (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
                <p className="text-sm font-medium flex items-center gap-1.5 text-red-700 dark:text-red-300">
                  <XCircle className="h-4 w-4" />
                  Generation Blocked
                </p>
                <p className="text-sm mt-1 text-red-600 dark:text-red-400">
                  {result.message}
                </p>
                {result.candidates > 0 && (
                  <p className="text-xs mt-1 text-red-500 dark:text-red-400">
                    {result.candidates} candidate bookings in period
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
                <p className="text-sm font-medium flex items-center gap-1.5 text-green-700 dark:text-green-300">
                  <CheckCircle2 className="h-4 w-4" />
                  Generation Complete ({result.period})
                </p>
                {/* Candidates denominator */}
                <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                  {result.candidates} candidate{result.candidates !== 1 ? 's' : ''} in period
                </p>
                {/* Full accounting breakdown */}
                <div className="flex gap-4 mt-2 text-sm flex-wrap">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    {result.created} created
                  </span>
                  <span className="flex items-center gap-1">
                    <SkipForward className="h-3.5 w-3.5 text-blue-500" />
                    {result.skipped_already_invoiced} already invoiced
                  </span>
                  {result.skipped_no_mapping > 0 && (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      {result.skipped_no_mapping} no mapping
                    </span>
                  )}
                  {result.skipped_cancelled > 0 && (
                    <span className="flex items-center gap-1">
                      <SkipForward className="h-3.5 w-3.5 text-gray-400" />
                      {result.skipped_cancelled} cancelled
                    </span>
                  )}
                  {result.skipped_other > 0 && (
                    <span className="flex items-center gap-1">
                      <SkipForward className="h-3.5 w-3.5 text-gray-400" />
                      {result.skipped_other} other skips
                    </span>
                  )}
                  {result.failed > 0 && (
                    <span className="flex items-center gap-1">
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                      {result.failed} failed
                    </span>
                  )}
                </div>
                {/* Accounting invariant */}
                {result.invariant_holds === false && (
                  <p className="text-xs mt-2 text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Accounting invariant broken: totals do not sum to candidates. A booking may be silently dropped.
                  </p>
                )}
              </div>
            )}

            {/* Failed bookings */}
            {result.details?.filter((d) => d.status === 'failed').length > 0 && (
              <div className="rounded-lg border p-3">
                <p className="text-sm font-medium mb-2">Failed bookings:</p>
                <div className="space-y-1 text-xs">
                  {result.details
                    .filter((d) => d.status === 'failed')
                    .map((d) => (
                      <div key={d.uplisting_id} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{d.uplisting_id}</span>
                          <span className="text-red-600 dark:text-red-400">{d.error}</span>
                        </div>
                        {d.pennylane_error && (
                          <p className="text-muted-foreground pl-1 break-all">
                            {d.pennylane_error}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Channel skip ledger (skipped_no_mapping details) */}
            {result.skip_ledger?.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
                <p className="text-sm font-medium mb-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  Skipped (no channel mapping)
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="pb-1 pr-3">Booking ID</th>
                        <th className="pb-1 pr-3">Channel (raw)</th>
                        <th className="pb-1 pr-3">Lookup key</th>
                        <th className="pb-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.skip_ledger.map((entry) => (
                        <tr key={entry.uplisting_id} className="border-b border-dashed last:border-0">
                          <td className="py-1 pr-3 font-mono">{entry.uplisting_id}</td>
                          <td className="py-1 pr-3 font-mono text-amber-700 dark:text-amber-300">{entry.channel_seen}</td>
                          <td className="py-1 pr-3 font-mono">{entry.channel_key}</td>
                          <td className="py-1 text-muted-foreground">{entry.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {result.skip_ledger[0]?.available_mapping_keys?.length > 0 && (
                  <p className="text-xs mt-2 text-muted-foreground">
                    Mapped channels: {result.skip_ledger[0].available_mapping_keys.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
